<template>
  <section
    v-if="sortedItems.length"
    ref="carousel"
    class="featured-carousel"
    aria-labelledby="featured-content-title"
    tabindex="0"
    @keydown.left.prevent="previous(true)"
    @keydown.right.prevent="next(true)"
    @mouseenter="hovered = true"
    @mouseleave="hovered = false"
    @focusin="focused = true"
    @focusout="handleFocusOut"
  >
    <v-container>
      <div class="featured-heading">
        <div>
          <p>Featured from the Reckon Way</p>
          <h2 id="featured-content-title">The stories worth stopping for</h2>
          <span>New adventures, favorite traditions, and the moments that become part of the Reckon story.</span>
        </div>
        <router-link v-if="viewAllHref" :to="viewAllHref" class="featured-view-all">
          {{ viewAllLabel }} <v-icon small>mdi-arrow-right</v-icon>
        </router-link>
      </div>

      <div
        class="featured-stage"
        :class="{ 'featured-stage-single': sortedItems.length === 1 }"
        @pointerdown="handlePointerDown"
        @pointerup="handlePointerUp"
        @pointercancel="resetPointer"
      >
        <div
          v-for="card in visibleCards"
          :key="`${card.position}-${card.item.id}`"
          class="featured-card-shell"
          :class="`featured-position-${card.position}`"
        >
          <FeaturedContentCard
            :item="card.item"
            :active="card.position === 'active'"
            :position-label="`${activeIndex + 1} of ${sortedItems.length}`"
            @activate="activate(card.index, true)"
          />
        </div>

        <template v-if="sortedItems.length > 1">
          <button
            type="button"
            class="featured-arrow featured-arrow-previous"
            aria-label="Show previous featured story"
            @click="previous(true)"
          >
            <v-icon>mdi-chevron-left</v-icon>
          </button>
          <button
            type="button"
            class="featured-arrow featured-arrow-next"
            aria-label="Show next featured story"
            @click="next(true)"
          >
            <v-icon>mdi-chevron-right</v-icon>
          </button>
        </template>
      </div>

      <div v-if="sortedItems.length > 1" class="featured-pagination" aria-label="Choose a featured story">
        <button
          v-for="(item, index) in sortedItems"
          :key="item.id"
          type="button"
          :class="{ active: index === activeIndex }"
          :aria-label="`Show slide ${index + 1}: ${item.title}`"
          :aria-current="index === activeIndex ? 'true' : null"
          @click="activate(index, true)"
        ></button>
      </div>

      <p class="sr-only" aria-live="polite" aria-atomic="true">
        {{ activeAnnouncement }}
      </p>
    </v-container>
  </section>
</template>

<script>
import FeaturedContentCard from './FeaturedContentCard.vue';

export default {
  name: 'FeaturedContentCarousel',
  components: { FeaturedContentCard },
  props: {
    items: { type: Array, default: () => [] },
    autoRotateMs: { type: Number, default: 7000 },
    viewAllHref: { type: [String, Object], default: null },
    viewAllLabel: { type: String, default: 'View All Stories' },
  },
  data: () => ({
    activeIndex: 0,
    timer: null,
    hovered: false,
    focused: false,
    userInteracted: false,
    reducedMotion: false,
    motionQuery: null,
    pointerStartX: null,
    ignoreActivationUntil: 0,
  }),
  computed: {
    sortedItems() {
      return this.items
        .filter((item) => item.featured)
        .slice()
        .sort((first, second) => {
          if (Boolean(first.pinned) !== Boolean(second.pinned)) return first.pinned ? -1 : 1;
          const firstPriority = first.featuredPriority ?? Number.MAX_SAFE_INTEGER;
          const secondPriority = second.featuredPriority ?? Number.MAX_SAFE_INTEGER;
          if (firstPriority !== secondPriority) return firstPriority - secondPriority;
          return new Date(second.publishedAt) - new Date(first.publishedAt);
        });
    },
    visibleCards() {
      if (this.sortedItems.length === 1) {
        return [{ item: this.sortedItems[0], index: 0, position: 'active' }];
      }

      const cards = [];
      if (this.sortedItems.length > 2) {
        const previousIndex = (this.activeIndex - 1 + this.sortedItems.length) % this.sortedItems.length;
        cards.push({ item: this.sortedItems[previousIndex], index: previousIndex, position: 'previous' });
      }
      cards.push({ item: this.sortedItems[this.activeIndex], index: this.activeIndex, position: 'active' });
      const nextIndex = (this.activeIndex + 1) % this.sortedItems.length;
      cards.push({ item: this.sortedItems[nextIndex], index: nextIndex, position: 'next' });
      return cards;
    },
    activeAnnouncement() {
      const item = this.sortedItems[this.activeIndex];
      return item ? `${item.title}, slide ${this.activeIndex + 1} of ${this.sortedItems.length}` : '';
    },
    shouldRotate() {
      return this.sortedItems.length > 1
        && !this.reducedMotion
        && !this.userInteracted
        && !this.hovered
        && !this.focused;
    },
  },
  watch: {
    shouldRotate: 'syncTimer',
    sortedItems: {
      handler() {
        this.setInitialSlide();
        this.syncTimer();
      },
      immediate: true,
    },
  },
  mounted() {
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.reducedMotion = this.motionQuery.matches;
    if (this.motionQuery.addEventListener) {
      this.motionQuery.addEventListener('change', this.handleMotionPreference);
    } else {
      this.motionQuery.addListener(this.handleMotionPreference);
    }
    this.syncTimer();
  },
  beforeDestroy() {
    this.clearTimer();
    if (!this.motionQuery) return;
    if (this.motionQuery.removeEventListener) {
      this.motionQuery.removeEventListener('change', this.handleMotionPreference);
    } else {
      this.motionQuery.removeListener(this.handleMotionPreference);
    }
  },
  methods: {
    setInitialSlide() {
      if (!this.sortedItems.length) {
        this.activeIndex = 0;
        return;
      }
      const pinnedIndex = this.sortedItems.findIndex((item) => item.pinned);
      if (pinnedIndex >= 0) {
        this.activeIndex = pinnedIndex;
        return;
      }
      const newestItem = this.sortedItems.reduce((newest, item) => (
        new Date(item.publishedAt) > new Date(newest.publishedAt) ? item : newest
      ));
      this.activeIndex = this.sortedItems.indexOf(newestItem);
    },
    activate(index, manual = false) {
      if (Date.now() < this.ignoreActivationUntil) return;
      if (manual) this.userInteracted = true;
      this.activeIndex = index;
    },
    previous(manual = false) {
      if (this.sortedItems.length < 2) return;
      if (manual) this.userInteracted = true;
      this.activeIndex = (this.activeIndex - 1 + this.sortedItems.length) % this.sortedItems.length;
    },
    next(manual = false) {
      if (this.sortedItems.length < 2) return;
      if (manual) this.userInteracted = true;
      this.activeIndex = (this.activeIndex + 1) % this.sortedItems.length;
    },
    syncTimer() {
      this.clearTimer();
      if (!this.shouldRotate || !this.autoRotateMs) return;
      this.timer = window.setInterval(() => this.next(false), this.autoRotateMs);
    },
    clearTimer() {
      if (!this.timer) return;
      window.clearInterval(this.timer);
      this.timer = null;
    },
    handleFocusOut(event) {
      if (!this.$refs.carousel.contains(event.relatedTarget)) this.focused = false;
    },
    handlePointerDown(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      this.pointerStartX = event.clientX;
    },
    handlePointerUp(event) {
      if (this.pointerStartX === null) return;
      const distance = event.clientX - this.pointerStartX;
      this.resetPointer();
      if (Math.abs(distance) < 45) return;
      this.ignoreActivationUntil = Date.now() + 250;
      if (distance < 0) this.next(true);
      else this.previous(true);
    },
    resetPointer() {
      this.pointerStartX = null;
    },
    handleMotionPreference(event) {
      this.reducedMotion = event.matches;
    },
  },
};
</script>

<style scoped>
.featured-carousel {
  background: #050505;
  color: #fff;
  outline: none;
  padding: 52px 0 62px;
}

.featured-carousel:focus-visible {
  box-shadow: inset 0 0 0 2px rgba(226, 50, 50, 0.78);
}

.featured-heading {
  align-items: flex-end;
  display: flex;
  gap: 24px;
  justify-content: space-between;
  margin-bottom: 26px;
}

.featured-heading p {
  color: #e23232;
  font-family: "Oswald", sans-serif;
  font-size: 0.82rem;
  font-weight: 900;
  letter-spacing: 0.1em;
  margin: 0 0 7px;
  text-transform: uppercase;
}

.featured-heading h2 {
  color: #fff;
  font-family: "Oswald", sans-serif;
  font-size: clamp(2rem, 4vw, 3.15rem);
  line-height: 1;
  margin: 0 0 10px;
}

.featured-heading span {
  color: #bdbdbd;
  display: block;
  font-size: 1rem;
  line-height: 1.5;
  max-width: 700px;
}

.featured-view-all {
  color: #fff;
  flex: 0 0 auto;
  font-family: "Oswald", sans-serif;
  font-size: 0.82rem;
  font-weight: 900;
  text-decoration: none;
  text-transform: uppercase;
}

.featured-view-all .v-icon {
  color: #e23232;
}

.featured-stage {
  align-items: center;
  display: grid;
  gap: 14px;
  grid-template-columns: minmax(0, 22fr) minmax(0, 56fr) minmax(0, 22fr);
  height: clamp(430px, 47vw, 570px);
  position: relative;
  touch-action: pan-y;
  user-select: none;
}

.featured-card-shell {
  height: 82%;
  min-width: 0;
  opacity: 0.88;
  transform: scale(0.91);
  transition: opacity 360ms ease, transform 360ms ease;
  z-index: 1;
}

.featured-position-previous {
  grid-column: 1;
}

.featured-position-active {
  grid-column: 2;
  height: 100%;
  opacity: 1;
  transform: scale(1);
  z-index: 3;
}

.featured-position-next {
  grid-column: 3;
}

.featured-stage-single .featured-position-active {
  grid-column: 2;
}

.featured-arrow {
  align-items: center;
  background: rgba(5, 5, 5, 0.78);
  border: 1px solid rgba(255, 255, 255, 0.26);
  border-radius: 50%;
  color: #fff;
  display: flex;
  height: 40px;
  justify-content: center;
  position: absolute;
  top: calc(50% - 20px);
  width: 40px;
  z-index: 5;
}

.featured-arrow:hover,
.featured-arrow:focus-visible {
  background: #e23232;
  border-color: #e23232;
  outline: 3px solid rgba(255, 255, 255, 0.68);
  outline-offset: 2px;
}

.featured-arrow .v-icon {
  color: inherit;
}

.featured-arrow-previous {
  left: calc(22% - 20px);
}

.featured-arrow-next {
  right: calc(22% - 20px);
}

.featured-pagination {
  display: flex;
  gap: 9px;
  justify-content: center;
  margin-top: 22px;
}

.featured-pagination button {
  background: #555;
  border: 0;
  border-radius: 999px;
  height: 7px;
  padding: 0;
  transition: background 180ms ease, width 180ms ease;
  width: 24px;
}

.featured-pagination button.active {
  background: #e23232;
  width: 42px;
}

.featured-pagination button:focus-visible {
  outline: 2px solid #fff;
  outline-offset: 4px;
}

.sr-only {
  clip: rect(0, 0, 0, 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
  width: 1px;
}

@media (max-width: 700px) {
  .featured-carousel {
    padding: 40px 0 48px;
  }

  .featured-heading {
    align-items: flex-start;
    flex-direction: column;
    margin-bottom: 20px;
  }

  .featured-stage {
    gap: 12px;
    grid-template-columns: minmax(0, calc(100% - 42px)) 30px;
    height: 510px;
    overflow: hidden;
  }

  .featured-position-previous {
    display: none;
  }

  .featured-position-active {
    grid-column: 1;
  }

  .featured-position-next {
    grid-column: 2;
    height: 90%;
    min-width: 220px;
    transform: none;
  }

  .featured-stage-single {
    display: block;
  }

  .featured-stage-single .featured-position-active {
    height: 100%;
  }

  .featured-arrow {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .featured-card-shell,
  .featured-pagination button {
    transition: none;
  }

  .featured-card-shell {
    transform: none;
  }
}
</style>
